import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Input,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { Building2, RefreshCw } from 'lucide-react';
import { avmApi, propertiesApi } from '../../services/api';

const { Text } = Typography;

interface IdealistaListing {
  id: string;
  zoneId: string;
  externalId?: string | null;
  price: number;
  size: number;
  pricePerM2: number;
  publishedAt: string;
  url?: string | null;
  title?: string | null;
  datasetId?: string | null;
  createdAt: string;
  zone?: {
    neighborhood?: string | null;
    zone?: string | null;
    municipality?: string | null;
  } | null;
}

const formatNumber = (value?: number | null, decimals = 0) =>
  typeof value === 'number'
    ? value.toLocaleString('es-ES', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : '-';

export default function AvmIdealistaListingsTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<IdealistaListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [datasetFilter, setDatasetFilter] = useState('');
  const [creatingPropertyId, setCreatingPropertyId] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { limit: 500 };
      if (search) params.search = search;
      if (zoneFilter) params.zoneId = zoneFilter;
      if (datasetFilter) params.datasetId = datasetFilter;
      const { data } = await avmApi.listIdealistaListings(params);
      setRows(data.data || []);
    } catch {
      message.error('No se pudo cargar el listado de testigos');
    } finally {
      setLoading(false);
    }
  }, [search, zoneFilter, datasetFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleDelete = async (row: IdealistaListing) => {
    try {
      await avmApi.deleteIdealistaListing(row.id);
      message.success('Testigo eliminado');
      fetchRows();
    } catch {
      message.error('No se pudo eliminar');
    }
  };

  const handleCreateProperty = useCallback(async (row: IdealistaListing) => {
    try {
      setCreatingPropertyId(row.id);
      const { data } = await propertiesApi.fromIdealistaListing(row.id);
      message.success('Propiedad creada (sin publicar)');
      navigate(`/properties/${data.id}`);
    } catch {
      message.error('No se pudo crear la propiedad');
    } finally {
      setCreatingPropertyId(null);
    }
  }, [navigate]);

  const columns = useMemo(
    () => [
      {
        title: 'ID_ZONA',
        dataIndex: 'zoneId',
        key: 'zoneId',
        width: 110,
        sorter: (a: IdealistaListing, b: IdealistaListing) =>
          a.zoneId.localeCompare(b.zoneId, 'es', { numeric: true }),
        defaultSortOrder: 'ascend' as const,
      },
      {
        title: 'Barrio',
        key: 'neighborhood',
        width: 160,
        render: (_: unknown, r: IdealistaListing) => r.zone?.neighborhood || '-',
      },
      {
        title: 'Zona',
        key: 'zone',
        width: 130,
        render: (_: unknown, r: IdealistaListing) => r.zone?.zone || '-',
      },
      {
        title: 'Publicado',
        dataIndex: 'publishedAt',
        key: 'publishedAt',
        width: 130,
        render: (v?: string) => (v ? dayjs(v).format('DD/MM/YYYY') : '-'),
        sorter: (a: IdealistaListing, b: IdealistaListing) =>
          new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime(),
      },
      {
        title: 'Precio',
        dataIndex: 'price',
        key: 'price',
        width: 140,
        render: (v: number) => `${formatNumber(v)} €`,
      },
      {
        title: 'Superficie',
        dataIndex: 'size',
        key: 'size',
        width: 110,
        render: (v: number) => `${formatNumber(v)} m²`,
      },
      {
        title: '€/m²',
        dataIndex: 'pricePerM2',
        key: 'pricePerM2',
        width: 130,
        render: (v: number) => `${formatNumber(v, 2)} €/m²`,
      },
      {
        title: 'Título',
        dataIndex: 'title',
        key: 'title',
        width: 220,
        render: (v?: string) => v || '-',
      },
      {
        title: 'URL',
        dataIndex: 'url',
        key: 'url',
        width: 220,
        render: (v?: string) =>
          v ? (
            <a href={v} target="_blank" rel="noreferrer">
              Abrir
            </a>
          ) : (
            '-'
          ),
      },
      {
        title: 'External ID',
        dataIndex: 'externalId',
        key: 'externalId',
        width: 140,
        render: (v?: string) => v || '-',
      },
      {
        title: 'Dataset',
        dataIndex: 'datasetId',
        key: 'datasetId',
        width: 160,
        render: (v?: string) => v || '-',
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 168,
        fixed: 'right' as const,
        render: (_: unknown, row: IdealistaListing) => (
          <Space size={4}>
            <Tooltip title="Crea una propiedad Swomey sin publicar">
              <Button
                size="small"
                type="primary"
                ghost
                icon={<Building2 size={13} />}
                loading={creatingPropertyId === row.id}
                onClick={() => handleCreateProperty(row)}
              >
                Crear prop.
              </Button>
            </Tooltip>
            <Popconfirm
              title="¿Eliminar testigo?"
              okText="Sí"
              cancelText="Cancelar"
              onConfirm={() => handleDelete(row)}
            >
              <Button size="small" danger>
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [creatingPropertyId, handleCreateProperty],
  );

  return (
    <>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Buscar (ID_ZONA, externalId, título, URL)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={() => fetchRows()}
            style={{ width: 360 }}
          />
          <Input
            allowClear
            placeholder="Filtrar por ID_ZONA"
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value)}
            onPressEnter={() => fetchRows()}
            style={{ width: 180 }}
          />
          <Input
            allowClear
            placeholder="Filtrar por datasetId"
            value={datasetFilter}
            onChange={(e) => setDatasetFilter(e.target.value)}
            onPressEnter={() => fetchRows()}
            style={{ width: 220 }}
          />
          <Button icon={<RefreshCw size={14} />} onClick={fetchRows}>
            Recargar
          </Button>
          <Text type="secondary">{rows.length} testigos</Text>
        </Space>
      </Card>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns as any}
        dataSource={rows}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 1900, y: 620 }}
        size="small"
      />
    </>
  );
}
